import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { isElectronApp } from "./config/config";
import PrivateRoute from "./helpers/PrivateRoute";
import LoginPage from "./views/LoginPage";
import DashboardLayout from "./views/DashboardLayout";
import DashboardPage from "./views/DashboardPage";
import POSPage from "./views/POSPage";
import OrdersPage from "./views/OrdersPage";
import DeliveryRoomRequestsPage from "./views/DeliveryRoomRequestsPage";
import KitchenPage from "./views/KitchenPage";
import ReservationPage from "./views/ReservationPage";
import CustomersPage from "./views/CustomersPage";
import InvoicesPage from "./views/InvoicesPage";
import UsersPage from "./views/UsersPage";
import PayrollPage from "./views/PayrollPage";
import ReportsPage from "./views/ReportsPage";
import KOTReportPage from './views/KOTReportPage';
import OrderDeletionReportPage from './views/OrderDeletionReportPage';
import DayBookReportPage from "./views/DayBookReportPage";
import SettingsPage from "./views/SettingsPage";
import SettingDetailsPage from "./views/SettingsViews/SettingDetailsPage";
import PrintSettingsPage from "./views/SettingsViews/PrintSettingsPage";
import PurchaseUnitsPage from "./views/SettingsViews/PurchaseUnitsPage";
import TablesSettingsPage from "./views/SettingsViews/TableSettingsPage";
import RoomsSettingsPage from "./views/SettingsViews/RoomsSettingsPage";
import MenuItemsSettingsPage from "./views/SettingsViews/MenuItemsSettingsPage";
import TaxSetupPage from "./views/SettingsViews/TaxSetupPage";
import PaymentTypesPage from "./views/SettingsViews/PaymentTypesPage";
import SuperAdminSettingsPage from "./views/SuperAdmin/SuperAdminSettingsPage";
import DevicesPage from "./views/SettingsViews/DevicesPage";
import ContactSupport from "./views/SettingsViews/ContactSupportPage";
import CategoriesPage from "./views/SettingsViews/CategoriesPage";
import MenuItemViewPage from "./views/SettingsViews/MenuItemViewPage";
import LoyaltySettingsPage from "./views/SettingsViews/LoyaltySettingsPage";
import CurrenciesSettingsPage from "./views/SettingsViews/CurrenciesSettingsPage";
import PrintersPage from "./views/SettingsViews/PrintersPage";
import { NavbarContext } from "./contexts/NavbarContext";
import { getIsNavbarCollapsed } from "./helpers/NavbarSettings";
import PrintReceiptPage from "./views/PrintReceiptPage";
import PrintKOTPage from "./views/KotPage";
import PrintTokenPage from "./views/PrintTokenPage";
import PrintPurchasePage from "./views/PrintPurchasePage";
import NoAccessPage from "./views/NoAccessPage";
import ProfilePage from "./views/ProfilePage";
import { SCOPES } from "./config/scopes";
import ScopeProtectedRoute from "./helpers/ScopeProtectedRoute";
import { SocketProvider } from "./contexts/SocketContext";
import RegistrationPage from "./views/RegistrationPage";
import ForgotPasswordPage from "./views/ForgotPasswordPage";
import InActiveSubscriptionPage from "./views/InActiveSubscriptionPage";
import PaymentSuccessPage from "./views/PaymentSuccessPage";
import PaymentCancelledPage from "./views/PaymentCancelledPage";
import ResetPasswordPage from "./views/ResetPasswordPage";
import QRMenuPage from "./views/QRMenuPage";
import SuperAdminProtectedRoute from "./helpers/SuperAdminProtectedRoute";
import SuperAdminLoginPage from "./views/SuperAdmin/LoginPage";
import SuperAdminDashboadLayout from "./views/SuperAdmin/SuperAdminDashboadLayout";
import SuperAdminDashboardPage from "./views/SuperAdmin/SuperAdminDashboardPage";
import SuperAdminContactSupportPage from "./views/SuperAdmin/SuperAdminContactSupportPage";
import SuperAdminTenantsPage from "./views/SuperAdmin/SuperAdminTenantsPage";
import SuperAdminReportsPage from "./views/SuperAdmin/SuperAdminReportsPage";
import SuperAdminTenantSubscriptionHistoryPage from "./views/SuperAdmin/SuperAdminTenantSubscriptionHistoryPage";
import WebsiteCRMPage from "./views/SuperAdmin/WebsiteCRMPage";
import ChatbotTrainingPage from "./views/SuperAdmin/ChatbotTrainingPage";
import ChatInboxPage from "./views/SuperAdmin/ChatInboxPage";
import PublicWebsitePage from "./views/PublicWebsitePage";
import CartPage from "./views/CartPage";
import OrderSuccessPage from "./views/OrderSuccessPage";
import OrderFailedPage from "./views/OrderFailedPage";
import CustomersImportPage from "./views/CustomersImportPage";
import FeedbackCollectPage from "./views/FeedbackCollectPage";
import FeedbackCollectSuccessPage from "./views/FeedbackCollectSuccessPage";
import FeedbackPage from "./views/FeedbackPage";
import LanguagePage from "./views/LanguagePage";
import InventoryPage from "./views/InventoryPage";
import UnitConversionsPage from "./views/UnitConversionsPage";
import InventoryLogsPage from "./views/InventoryLogsPage";
import InventoryDashboardPage from "./views/InventoryDashboard";
import SuppliersPage from "./views/SuppliersPage";
import PurchasesPage from "./views/PurchasesPage";
import AccountsPayablePage from "./views/AccountsPayablePage";
import AccountsReceivablePage from "./views/AccountsReceivablePage";
import ProfitLossReportPage from "./views/ProfitLossReportPage";
import TrialBalanceReportPage from "./views/TrialBalanceReportPage";
import BalanceSheetReportPage from "./views/BalanceSheetReportPage";
import LedgerReportPage from "./views/LedgerReportPage";
import { ThemeProvider } from "./contexts/ThemeContext";
import MyToaster from "./components/MyToaster";
import MenuItemsImportPage from "./views/MenuItemsImportPage";
import MenuWithInventoryImportPage from "./views/SettingsViews/MenuWithInventoryImportPage";
import InventoryImportPage from "./views/InventoryImportPage";
import VoidedInvoicesReportPage from "./views/VoidedInvoicesReportPage";
import ExpensesPage from "./views/ExpensesPage";
import SyncEngine from "./services/SyncEngine";
import RoomsStatusPage from "./views/RoomsStatusPage";
import HousekeepingPage from "./views/HousekeepingPage";
import LaundryPage from "./views/LaundryPage";
import BanksPage from "./views/BanksPage";
import GenericReportPage from "./views/GenericReportPage";
import AssetsPage from "./views/AssetsPage";
import DiscountRoundOffReportPage from "./views/DiscountRoundOffReportPage";
import { unlockAudio } from "./helpers/SoundHelper";
import { isTokenLikelyExpired, getUserDetailsInLocalStorage } from "./helpers/UserDetails";
import { clearAllAuthData } from "./helpers/ApiClient";

export default function App() {
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(
    getIsNavbarCollapsed()
  );

  useEffect(() => {
    SyncEngine.start();

    // Periodic token expiration check (auto-logout if token expired)
    const tokenCheckInterval = setInterval(() => {
      const user = getUserDetailsInLocalStorage();
      if (user?.token && isTokenLikelyExpired()) {
        console.log("Token expired during active session, logging out...");
        clearAllAuthData(user.role);
      }
    }, 15000);

    // Auto-unlock Web Audio API and HTML5 audio on first user gesture anywhere
    const handleFirstGesture = () => {
      unlockAudio();
    };
    window.addEventListener("click", handleFirstGesture, { once: true, capture: true });
    window.addEventListener("touchstart", handleFirstGesture, { once: true, capture: true });
    window.addEventListener("keydown", handleFirstGesture, { once: true, capture: true });

    return () => {
      SyncEngine.stop();
      clearInterval(tokenCheckInterval);
      window.removeEventListener("click", handleFirstGesture, { capture: true });
      window.removeEventListener("touchstart", handleFirstGesture, { capture: true });
      window.removeEventListener("keydown", handleFirstGesture, { capture: true });
    };
  }, []);

  return (
    <SocketProvider>
    <ThemeProvider>
    <NavbarContext.Provider value={[isNavbarCollapsed, setIsNavbarCollapsed]}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={isElectronApp() ? <Navigate to="/login" replace /> : <PublicWebsitePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/print-receipt" element={<PrintReceiptPage />} />
          <Route path="/print-kot" element={<PrintKOTPage/>} />
          <Route path="/print-token" element={<PrintTokenPage />} />
          <Route path="/print-purchase" element={<PrintPurchasePage />} />
          <Route path="/no-access" element={<NoAccessPage />} />
          <Route path="/success" element={<PaymentSuccessPage />} />
          <Route path="/cancelled-payment" element={<PaymentCancelledPage />} />

          <Route path="/dashboard/inactive-subscription" element={<InActiveSubscriptionPage />} />

          <Route path="/m/:qrcode" element={<QRMenuPage />} />
          <Route path="/m/:qrcode/cart" element={<CartPage />} />
          <Route path="/m/order-success" element={<OrderSuccessPage />} />
          <Route path="/m/order-failed" element={<OrderFailedPage />} />

          <Route path="/m/:qrcode/feedback" element={<FeedbackCollectPage />} />
          <Route path="/m/:qrcode/feedback/success" element={<FeedbackCollectSuccessPage />} />


          {/* app routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route
              path=""
              element={
                <ScopeProtectedRoute scopes={[SCOPES.DASHBOARD]}>
                  <DashboardPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="home"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.DASHBOARD]}>
                  <DashboardPage />
                </ScopeProtectedRoute>
              }
            />
            <Route path="profile" element={<ProfilePage />} />

            <Route
              path="pos"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.POS]}>
                  <POSPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="orders"
              element={
                <ScopeProtectedRoute
                  scopes={[
                    SCOPES.POS,
                    SCOPES.ORDERS,
                    SCOPES.ORDER_STATUS,
                    SCOPES.ORDER_STATUS_DISPLAY,
                  ]}
                >
                  <OrdersPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="requests"
              element={
                <ScopeProtectedRoute
                  scopes={[
                    SCOPES.POS,
                    SCOPES.ORDERS,
                  ]}
                >
                  <DeliveryRoomRequestsPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="kitchen"
              element={
                <ScopeProtectedRoute
                  scopes={[SCOPES.KITCHEN, SCOPES.KITCHEN_DISPLAY]}
                >
                  <KitchenPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reservation"
              element={
                <ScopeProtectedRoute
                  scopes={[
                    SCOPES.RESERVATIONS,
                    SCOPES.VIEW_RESERVATIONS,
                    SCOPES.MANAGE_RESERVATIONS,
                  ]}
                >
                  <ReservationPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="customers"
              element={
                <ScopeProtectedRoute
                  scopes={[
                    SCOPES.CUSTOMERS,
                    SCOPES.VIEW_CUSTOMERS,
                    SCOPES.MANAGE_CUSTOMERS,
                  ]}
                >
                  <CustomersPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="customers/import"
              element={
                <ScopeProtectedRoute
                  scopes={[
                    SCOPES.CUSTOMERS,
                    SCOPES.VIEW_CUSTOMERS,
                    SCOPES.MANAGE_CUSTOMERS,
                  ]}
                >
                  <CustomersImportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="invoices"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVOICES]}>
                  <InvoicesPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY]}>
                  <InventoryPage/>
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory/import"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.MANAGE_INVENTORY]}>
                  <InventoryImportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory/dashboard"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY]}>
                  <InventoryDashboardPage/>
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory/stock-movements"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY]}>
                  <InventoryLogsPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory/suppliers"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.SUPPLIERS]}>
                  <SuppliersPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory/purchases"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.PURCHASES, SCOPES.PURCHASE_INVOICES]}>
                  <PurchasesPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="inventory/accounts-payable"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVENTORY, SCOPES.PURCHASES, SCOPES.PURCHASE_INVOICES, SCOPES.ACCOUNTING]}>
                  <AccountsPayablePage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="accounts-receivable"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.INVOICES, SCOPES.ACCOUNTING, SCOPES.REPORTS]}>
                  <AccountsReceivablePage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="expenses"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.EXPENSES, SCOPES.REPORTS, SCOPES.INVENTORY]}>
                  <ExpensesPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="pms/rooms"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.PMS, SCOPES.ROOMS]}>
                  <RoomsStatusPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="pms/housekeeping"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.PMS, SCOPES.HOUSEKEEPING]}>
                  <HousekeepingPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="pms/laundry"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.PMS, SCOPES.LAUNDRY]}>
                  <LaundryPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="accounting/banks"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.ACCOUNTING, SCOPES.INVOICES, SCOPES.SETTINGS]}>
                  <BanksPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="assets"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.ASSETS, SCOPES.SETTINGS, SCOPES.INVENTORY]}>
                  <AssetsPage />
                </ScopeProtectedRoute>
              }
            />
            <Route path="users" element={<ScopeProtectedRoute scopes={[SCOPES.SETTINGS]}><UsersPage /></ScopeProtectedRoute>} />
            <Route path="payroll" element={<ScopeProtectedRoute scopes={[SCOPES.PAYROLL, SCOPES.SETTINGS]}><PayrollPage /></ScopeProtectedRoute>} />
            <Route
              path="reports"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <ReportsPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/day-book"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS, SCOPES.DAYBOOK]}>
                  <DayBookReportPage />
                </ScopeProtectedRoute>
              }
            />
              <Route
                path="reports/profit-loss"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <ProfitLossReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/trial-balance"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <TrialBalanceReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/balance-sheet"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <BalanceSheetReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/ledger"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <LedgerReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/voided-invoices"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <VoidedInvoicesReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/kot-report"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <KOTReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/order-deletion-report"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <OrderDeletionReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/discounts-deductions"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <DiscountRoundOffReportPage />
                </ScopeProtectedRoute>
              }
            />
            <Route
              path="reports/:reportType"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.REPORTS]}>
                  <GenericReportPage />
                </ScopeProtectedRoute>
              }
            />

            <Route
              path="feedbacks"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.FEEDBACK]}>
                  <FeedbackPage />
                </ScopeProtectedRoute>
              }
            />

            <Route path="devices" element={<DevicesPage />} />
            <Route path="contact-support" element={<ContactSupport />} />
            <Route path="language" element={<LanguagePage />} />

            <Route
              path="settings"
              element={
                <ScopeProtectedRoute scopes={[SCOPES.SETTINGS]}>
                  <SettingsPage />
                </ScopeProtectedRoute>
              }
            >
              <Route path="" element={<SettingDetailsPage />} />
              <Route path="details" element={<SettingDetailsPage />} />
              <Route path="store-details" element={<SettingDetailsPage />} />
              <Route path="workflow" element={<SettingDetailsPage />} />
              <Route path="application-workflow" element={<SettingDetailsPage />} />
              <Route path="sync" element={<SettingDetailsPage />} />
              <Route path="print-settings" element={<PrintSettingsPage />} />
              <Route path="purchase-units" element={<PurchaseUnitsPage />} />
              <Route path="tables" element={<TablesSettingsPage />} />
              <Route path="rooms" element={<RoomsSettingsPage />} />
              <Route path="menu-items" element={<MenuItemsSettingsPage />} />
              <Route path="menu-items/import" element={<MenuItemsImportPage />} />
              <Route path="menu-items/bulk-with-inventory" element={<MenuWithInventoryImportPage />} />
              <Route path="menu-items/:id" element={<MenuItemViewPage />} />
              <Route
                path="menu-items/categories"
                element={<CategoriesPage />}
              />
              <Route path="tax-setup" element={<TaxSetupPage />} />
              <Route path="payment-types" element={<PaymentTypesPage />} />
              <Route path="loyalty" element={<LoyaltySettingsPage />} />
              <Route path="currencies" element={<CurrenciesSettingsPage />} />
              <Route path="printers" element={<PrintersPage />} />

            </Route>
          </Route>
          {/* app routes */}


          {/* superadmin routes */}
          <Route path="/superadmin" element={<SuperAdminLoginPage />} />
          <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
          <Route path="/superadmin/dashboard" element={<PrivateRoute><SuperAdminDashboadLayout/></PrivateRoute>}>
            <Route path="" element={<SuperAdminProtectedRoute><SuperAdminDashboardPage /></SuperAdminProtectedRoute>} />
            <Route path="home" element={<SuperAdminProtectedRoute><SuperAdminDashboardPage /></SuperAdminProtectedRoute>} />
            <Route path="tenants" element={<SuperAdminProtectedRoute><SuperAdminTenantsPage /></SuperAdminProtectedRoute>} />
            <Route path="tenants/:id/subscription-history" element={<SuperAdminProtectedRoute><SuperAdminTenantSubscriptionHistoryPage /></SuperAdminProtectedRoute>} />
            <Route path="reports" element={<SuperAdminProtectedRoute><SuperAdminReportsPage /></SuperAdminProtectedRoute>} />
            <Route path="contact-support" element={<SuperAdminProtectedRoute><SuperAdminContactSupportPage /></SuperAdminProtectedRoute>} />
            <Route path="website-crm" element={<SuperAdminProtectedRoute><WebsiteCRMPage /></SuperAdminProtectedRoute>} />
            <Route path="chatbot-training" element={<SuperAdminProtectedRoute><ChatbotTrainingPage /></SuperAdminProtectedRoute>} />
            <Route path="chat-inbox" element={<SuperAdminProtectedRoute><ChatInboxPage /></SuperAdminProtectedRoute>} />
            <Route path="settings" element={<SuperAdminProtectedRoute><SuperAdminSettingsPage /></SuperAdminProtectedRoute>} />
            <Route path="language" element={<LanguagePage />} />
          </Route>
          {/* superadmin routes */}


        </Routes>
        {/* <Toaster  /> */}
        <MyToaster />
      </BrowserRouter>
    </NavbarContext.Provider>
    </ThemeProvider>
    </SocketProvider>
  );
}
